import React, {useState, useCallback} from 'react';
import {View, StyleSheet, FlatList, TouchableOpacity, ActivityIndicator, LayoutAnimation, Platform, UIManager} from 'react-native';
import {Text, Button, Snackbar} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useFocusEffect} from '@react-navigation/native';
import {colors, spacing} from '../../theme';
import {paymentApi} from '../../services/api/paymentApi';
import PayWorkerModal from './PayWorkerModal';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const WorkerDuesScreen = () => {
  const {t} = useTranslation();
  const [dues, setDues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});
  const [payModal, setPayModal] = useState({visible: false, workerId: null, workerName: '', items: []});
  const [expandedIds, setExpandedIds] = useState({});

  const toggleExpand = (workerId) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedIds(prev => ({...prev, [workerId]: !prev[workerId]}));
  };

  const fetchDues = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const res = await paymentApi.getDues();
      setDues(res.data?.data || []);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to load dues';
      setSnackbar({visible: true, message: msg});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchDues();
    }, [fetchDues]),
  );

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDues(false);
  };

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
  };

  const renderWorkerCard = ({item: group}) => {
    const isExpanded = !!expandedIds[group.workerId];
    return (
      <View style={styles.card}>
        {/* Summary row — always visible */}
        <TouchableOpacity
          activeOpacity={0.7}
          style={styles.cardHeader}
          onPress={() => toggleExpand(group.workerId)}>
          <View style={styles.headerLeft}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {group.workerName?.charAt(0)?.toUpperCase() || '?'}
              </Text>
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.workerName}>{group.workerName}</Text>
              <View style={styles.headerMeta}>
                {!!group.workerTypeName && (
                  <Text style={styles.workerType}>{group.workerTypeName}</Text>
                )}
                <View style={styles.expandHint}>
                  <Text style={styles.itemCount}>{group.items?.length || 0} items</Text>
                  <MaterialCommunityIcons
                    name={isExpanded ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color={colors.textSecondary}
                  />
                </View>
              </View>
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.totalAmount}>{'\u20B9'}{Number(group.totalDue || 0).toLocaleString('en-IN')}</Text>
            <Button
              mode="contained"
              compact
              style={styles.payButton}
              labelStyle={styles.payButtonLabel}
              onPress={() =>
                setPayModal({
                  visible: true,
                  workerId: group.workerId,
                  workerName: group.workerName,
                  items: group.items,
                })
              }>
              Pay
            </Button>
          </View>
        </TouchableOpacity>

        {/* Expanded detail rows */}
        {isExpanded && (
          <View style={styles.detailSection}>
            {group.items.map((item, idx) => {
              const isTask = item.type === 'Task';
              return (
                <View
                  key={item.instanceTaskId ? `t-${item.instanceTaskId}` : `w-${item.workAssignmentId}`}
                  style={[styles.itemRow, idx === group.items.length - 1 && {borderBottomWidth: 0}]}>
                  <View style={styles.itemLeft}>
                    <View style={[styles.typeDot, {backgroundColor: isTask ? colors.primary : colors.warning}]} />
                    <View>
                      <Text style={styles.itemName} numberOfLines={1}>{item.taskTypeName}</Text>
                      <View style={styles.itemMeta}>
                        {item.orderNumber && (
                          <Text style={styles.itemMetaText}>#{item.orderNumber}</Text>
                        )}
                        {item.completedAt && (
                          <Text style={styles.itemMetaText}>{formatDate(item.completedAt)}</Text>
                        )}
                      </View>
                    </View>
                  </View>
                  <Text style={styles.itemAmount}>{'\u20B9'}{item.amount || 0}</Text>
                </View>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  if (loading && dues.length === 0) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={dues}
        keyExtractor={item => String(item.workerId)}
        renderItem={renderWorkerCard}
        contentContainerStyle={styles.listContent}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        ListHeaderComponent={dues.length > 0 ? (
          <View style={styles.legendRow}>
            <View style={[styles.typeDot, {backgroundColor: colors.primary}]} />
            <Text style={styles.legendText}>Task</Text>
            <View style={[styles.typeDot, {backgroundColor: colors.warning, marginLeft: spacing.md}]} />
            <Text style={styles.legendText}>Dress Assignment</Text>
          </View>
        ) : null}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="cash-check" size={48} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('common.noData')}</Text>
          </View>
        }
      />

      {payModal.visible && (
        <PayWorkerModal
          visible={payModal.visible}
          onDismiss={() => setPayModal({visible: false, workerId: null, workerName: '', items: []})}
          workerName={payModal.workerName}
          workerId={payModal.workerId}
          items={payModal.items}
          onPaymentComplete={() => fetchDues(false)}
        />
      )}

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  centerContent: {justifyContent: 'center', alignItems: 'center'},
  listContent: {padding: spacing.sm, paddingBottom: spacing.xl, gap: spacing.sm},

  // Card
  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
  },
  headerLeft: {flexDirection: 'row', alignItems: 'center', flex: 1, gap: spacing.sm},
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {fontSize: 16, fontWeight: '700', color: colors.primary},
  headerInfo: {flex: 1},
  workerName: {fontSize: 15, fontWeight: '700', color: colors.text},
  headerMeta: {flexDirection: 'row', gap: spacing.sm, marginTop: 2},
  workerType: {fontSize: 11, color: colors.textSecondary},
  itemCount: {fontSize: 11, color: colors.textSecondary},
  expandHint: {flexDirection: 'row', alignItems: 'center', gap: 2},
  headerRight: {alignItems: 'flex-end', gap: 4},
  totalAmount: {fontSize: 17, fontWeight: '800', color: colors.error},
  payButton: {
    backgroundColor: colors.primary,
    borderRadius: 16,
    paddingHorizontal: 4,
  },
  payButtonLabel: {fontSize: 12, fontWeight: '600', marginVertical: 2, marginHorizontal: spacing.sm},

  // Expanded detail
  detailSection: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.divider,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingLeft: spacing.md + 48,
    paddingRight: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  itemLeft: {flexDirection: 'row', alignItems: 'center', flex: 1, gap: spacing.sm},
  typeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  itemName: {fontSize: 13, fontWeight: '500', color: colors.text},
  itemMeta: {flexDirection: 'row', gap: spacing.sm, marginTop: 2},
  itemMetaText: {fontSize: 11, color: colors.textSecondary},
  itemAmount: {fontSize: 14, fontWeight: '700', color: colors.text},

  // Legend
  legendRow: {flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, marginBottom: spacing.xs},
  legendText: {fontSize: 11, color: colors.textSecondary},

  // Empty
  emptyContainer: {
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyText: {
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontSize: 14,
  },
});

export default WorkerDuesScreen;
